import { ColumnDef } from "@tanstack/react-table";
import moment from "moment";
import { useMemo } from "react";
import { useAuthContext } from "providers/Auth";
import { formatToCurrency } from "shared/utils";
import {
  RentReportingData,
  EnrollmentCase
} from "views/rent-reporting-id/types";
import { PaymentStatusElement } from "../components/PaymentStatusElement";
import {
  formatPropertyName,
  formatAddress,
  buildDelinquencyOptions
} from "../utils";

export const useTableColumns = (
  selectedEnrollmentCase?: EnrollmentCase
): ColumnDef<RentReportingData, any>[] => {
  const { type } = useAuthContext();

  return useMemo<ColumnDef<RentReportingData, any>[]>(() => {
    const baseColumns: ColumnDef<RentReportingData, any>[] = [
      {
        id: "property_name fixed_width",
        accessorKey: "property_name",
        header: "Property Name",
        size: 160,
        cell: () => (
          <span className="text-sm text-content-1000 font-medium">
            {formatPropertyName(selectedEnrollmentCase)}
          </span>
        )
      },
      {
        id: "property_address fixed_width",
        accessorKey: "property_address",
        header: "Address",
        size: 220,
        cell: () => (
          <span className="text-sm text-content-1000 font-medium">
            {formatAddress(selectedEnrollmentCase)}
          </span>
        )
      },
      {
        id: "for_month fixed_width",
        accessorKey: "for_month",
        header: "Month",
        size: 100,
        cell: (p) => (
          <span className="text-sm text-content-1000 font-medium">
            {p.getValue() ? moment(p.getValue()).format("MMM YYYY") : ""}
          </span>
        )
      },
      {
        id: "rent_amount fixed_width",
        accessorKey: "rent_amount",
        header: "Rent",
        size: 110,
        cell: (p) => (
          <p className="text-sm text-content-1000 font-medium">
            {p.getValue() ? formatToCurrency(p.getValue()) : "-"}
          </p>
        )
      }
    ];

    if (!type.isSDK)
      baseColumns.push({
        id: "status fixed_width",
        accessorKey: "",
        header: "Payment status",
        size: 200,
        cell: (p) => {
          const options = buildDelinquencyOptions(
            p.row.original.available_delinquency_options
          );
          return (
            <PaymentStatusElement
              status={{
                status: p.row.original.status,
                months_delinquency: p.row.original.months_delinquency
              }}
              options={options}
              verificationId={p.row.original.id}
            />
          );
        }
      });

    return baseColumns;
  }, [type, selectedEnrollmentCase]);
};
